/**
 * AdminMarketingInviteCard — Send WhatsApp invites to prospects.
 *
 * Single textarea: admin pastes ONE or MANY phone numbers separated by
 * space / comma / newline / etc. Common recipient name applies to all.
 * Successful sends are saved to a persistent "Contact book" tab so admin
 * can resend / build campaigns later.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  PaperPlaneTilt, WhatsappLogo, CheckCircle, XCircle, Spinner, ClockClockwise,
  AddressBook, MagnifyingGlass, ArrowsClockwise, Trash,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

// Mirror the backend regex — strip everything that isn't a digit or
// separator, then split.
const splitTokens = (raw) => (raw || '').split(/[\s,;|/]+/).filter(Boolean);

// Quick client-side preview of what backend will actually send (10-digit
// Indian normalisation: strip +91 / leading 0).
const normPhone = (tok) => {
  let d = (tok || '').replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return (d.length === 10 && /^\d{10}$/.test(d)) ? d : null;
};

const AdminMarketingInviteCard = () => {
  const [tab, setTab] = useState('compose'); // compose | history | contacts
  const [name, setName] = useState('');
  const [phonesRaw, setPhonesRaw] = useState('');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState({ items: [], total: 0, success_count: 0 });
  const [contacts, setContacts] = useState({ items: [], total: 0, segments: [], cities: [] });
  const [contactQuery, setContactQuery] = useState('');
  const [segmentFilter, setSegmentFilter] = useState(''); // '' = all professions
  const [cityFilter, setCityFilter] = useState('');       // '' = all cities
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [sendingSegment, setSendingSegment] = useState(false);

  // Cleanup-rule inline form (toggled from "Cleanup rule" link in the
  // segment toolbar). Carries one set of rules at a time; defaults to
  // the user-defined Decorator preset when the active segment is "decorator".
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupRule, setCleanupRule] = useState({
    remove_if_has: '',
    keep_if_has: '',
    require_any_of: '',
    keep_if_tamil: false,
  });
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupRunning, setCleanupRunning] = useState(false);

  // Segment merge mode — admin multi-selects chips → picks one keeper →
  // every contact with the selected source tags gets them rewritten to
  // the keeper. Lets us collapse near-duplicates like
  // "wedding venues" / "Hotel venues" / "Villa venues" → "venues".
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelected, setMergeSelected] = useState(new Set());
  const [mergeTarget, setMergeTarget] = useState('');
  const [mergeRunning, setMergeRunning] = useState(false);

  // Per-segment presets. Pick "decorator" → auto-load your stated rule.
  const SEGMENT_PRESETS = {
    decorator: {
      remove_if_has: 'interior, painting',
      keep_if_has: 'wedding, event, balloon',
      require_any_of: 'decorator, event',
      keep_if_tamil: true,
    },
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/marketing/invites?limit=30`,
        { withCredentials: true },
      );
      setHistory(data || { items: [], total: 0, success_count: 0 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load history');
    }
    setLoadingHistory(false);
  };

  const loadContacts = async () => {
    setLoadingContacts(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (contactQuery) params.set('q', contactQuery);
      if (segmentFilter) params.set('profession', segmentFilter);
      if (cityFilter) params.set('city', cityFilter);
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/marketing/contacts?${params.toString()}`,
        { withCredentials: true },
      );
      setContacts(data || { items: [], total: 0, segments: [], cities: [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load contacts');
    }
    setLoadingContacts(false);
  };

  useEffect(() => { loadHistory(); loadContacts(); }, []);
  useEffect(() => {
    if (tab === 'contacts') {
      const t = setTimeout(loadContacts, 250);
      return () => clearTimeout(t);
    }
  }, [contactQuery, segmentFilter, cityFilter, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live preview of parsed numbers
  const allTokens = splitTokens(phonesRaw);
  const validSet = new Set();
  const validList = [];
  const invalid = [];
  allTokens.forEach((t) => {
    const n = normPhone(t);
    if (n && !validSet.has(n)) { validSet.add(n); validList.push(n); }
    else if (!n) invalid.push(t);
  });
  const canSend = name.trim().length > 0 && validList.length > 0 && !sending;

  const send = async () => {
    if (!canSend) {
      toast.error('Enter name + at least one valid 10-digit number');
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/marketing/invite-bulk`,
        { name: name.trim(), phones: phonesRaw },
        { withCredentials: true },
      );
      if (data?.sent_ok === data?.valid_count) {
        toast.success(`All ${data.sent_ok} invites sent`);
      } else {
        toast.warning(`${data?.sent_ok}/${data?.valid_count} sent. ${data?.failed} failed — see History.`);
      }
      setPhonesRaw('');
      await Promise.all([loadHistory(), loadContacts()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    }
    setSending(false);
  };

  const resendFromContact = async (c) => {
    if (!name.trim()) {
      setName(c.name || '');
    }
    setPhonesRaw((cur) => cur ? `${cur} ${c.phone}` : c.phone);
    setTab('compose');
    toast.info(`Added +91 ${c.phone} to the send list`);
  };

  const deleteContact = async (c) => {
    if (!window.confirm(`Remove ${c.name || 'this contact'} (+91 ${c.phone}) from the Contact Book?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/marketing/contacts/${c.phone}`,
        { withCredentials: true });
      toast.success(`Removed +91 ${c.phone}`);
      await loadContacts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const deleteAllContacts = async () => {
    const total = contacts?.total ?? 0;
    if (total === 0) { toast.info('Contact book is already empty'); return; }
    const typed = window.prompt(
      `This will permanently delete ALL ${total} contacts from the book.\n\nType DELETE to confirm:`
    );
    if (typed !== 'DELETE') {
      if (typed !== null) toast.error('Confirmation text did not match — nothing deleted');
      return;
    }
    try {
      const { data } = await axios.delete(
        `${BACKEND_URL}/api/admin/marketing/contacts?confirm=DELETE_ALL`,
        { withCredentials: true },
      );
      toast.success(`Deleted ${data?.deleted ?? 0} contacts`);
      await loadContacts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk delete failed');
    }
  };

  const purgeLandlines = async () => {
    if (!window.confirm(
      'Remove every contact whose number is NOT a 10-digit mobile starting with 6/7/8/9 (landlines, broken entries)?\n\nReachable mobile contacts will be kept.'
    )) return;
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/marketing/contacts/purge-landlines`,
        null, { withCredentials: true },
      );
      const n = data?.deleted ?? 0;
      if (n === 0) toast.info('No landline / broken-format contacts found.');
      else toast.success(`Removed ${n} landline / non-mobile contact${n === 1 ? '' : 's'}.`);
      await loadContacts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cleanup failed');
    }
  };

  const purgeHospitality = async () => {
    if (!window.confirm(
      'Remove every contact whose name contains "hotel / restaurant / cafe / bar / rooms" UNLESS it also says "chef / venue / caterer / catering / wedding / event"?\n\nThis cleans up unrelated hospitality listings that slipped into the book.'
    )) return;
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/marketing/contacts/purge-hospitality`,
        null, { withCredentials: true },
      );
      const n = data?.deleted ?? 0;
      if (n === 0) toast.info('No hospitality-only contacts found.');
      else toast.success(`Removed ${n} hotel / cafe / bar / restaurant contact${n === 1 ? '' : 's'}.`);
      await loadContacts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cleanup failed');
    }
  };
  const purgeJoined = async () => {
    if (!window.confirm(
      'Remove every contact whose phone matches a registered vendor / professional account on BeeWeddy?\n\nUseful for keeping the marketing list focused on people who haven\'t joined yet.'
    )) return;
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/marketing/contacts/purge-joined`,
        null, { withCredentials: true },
      );
      const n = data?.deleted ?? 0;
      if (n === 0) toast.info(`No contacts matched any of the ${data.joined_users_checked} joined vendor/pro accounts.`);
      else toast.success(`Removed ${n} contact${n === 1 ? '' : 's'} who already joined (${data.joined_users_checked} accounts checked).`);
      await loadContacts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cleanup failed');
    }
  };

  const backfillCities = async () => {
    if (!window.confirm(
      'Group every saved contact by city using the address that Google Maps returned?\n\nReads each contact\'s address, extracts city names (Chennai, Bangalore, …), and tags the contact so you can blast city-wise.'
    )) return;
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/marketing/contacts/backfill-cities`,
        null, { withCredentials: true, timeout: 120000 },
      );
      const top = Object.entries(data?.city_counts || {}).slice(0, 3).map(([c, n]) => `${c}·${n}`).join(', ');
      toast.success(`Tagged ${data?.tagged ?? 0}/${data?.scanned ?? 0} contacts. Top: ${top}.`, { duration: 9000 });
      await loadContacts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Backfill failed');
    }
  };


  const openCleanupRule = (segment) => {
    const preset = SEGMENT_PRESETS[segment?.toLowerCase()] || null;
    if (preset) {
      setCleanupRule({
        remove_if_has: preset.remove_if_has || '',
        keep_if_has: preset.keep_if_has || '',
        require_any_of: preset.require_any_of || '',
        keep_if_tamil: !!preset.keep_if_tamil,
      });
    } else {
      setCleanupRule({ remove_if_has: '', keep_if_has: '', require_any_of: '', keep_if_tamil: false });
    }
    setCleanupPreview(null);
    setCleanupOpen(true);
  };

  const _parseRuleList = (s) => (s || '')
    .split(/[,;\n|]+/).map((x) => x.trim()).filter(Boolean);

  const toggleMergeMode = () => {
    setMergeMode((m) => {
      if (m) { setMergeSelected(new Set()); setMergeTarget(''); }
      return !m;
    });
  };

  const toggleMergePick = (segName) => {
    setMergeSelected((prev) => {
      const next = new Set(prev);
      if (next.has(segName)) next.delete(segName);
      else next.add(segName);
      // Auto-suggest the shortest selected name as the canonical keeper
      // ("venues" > "wedding venues") — admin can override.
      const arr = Array.from(next);
      if (arr.length && !mergeTarget) {
        setMergeTarget(arr.reduce((a, b) => (b.length < a.length ? b : a), arr[0]));
      } else if (arr.length === 0) {
        setMergeTarget('');
      }
      return next;
    });
  };

  const runSegmentMerge = async () => {
    const sources = Array.from(mergeSelected);
    const target = (mergeTarget || '').trim();
    if (sources.length < 2) {
      toast.error('Pick at least 2 segments to merge.');
      return;
    }
    if (!target) { toast.error('Type a target segment name.'); return; }
    const expected = sources
      .filter((s) => s.toLowerCase() !== target.toLowerCase())
      .reduce((acc, s) => acc + (contacts.segments.find((x) => x.profession === s)?.count || 0), 0);
    if (!window.confirm(
      `Merge ${sources.length} segments into "${target}"?\n\nUp to ${expected} contact tag${expected === 1 ? '' : 's'} will be rewritten.\nNo contacts are deleted — only their profession labels change.`
    )) return;
    setMergeRunning(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/marketing/segments/merge`,
        { sources, target }, { withCredentials: true },
      );
      toast.success(
        `Merged ${data.contacts_affected} contact${data.contacts_affected === 1 ? '' : 's'} → "${data.target}" (${data.target_count_after} total in segment).`,
        { duration: 8000 },
      );
      // If we were filtering by one of the now-removed source tags, switch
      // to the new merged target so the user keeps seeing matching rows.
      if (segmentFilter && sources.includes(segmentFilter)) setSegmentFilter(data.target);
      setMergeSelected(new Set());
      setMergeTarget('');
      setMergeMode(false);
      await loadContacts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Merge failed');
    } finally {
      setMergeRunning(false);
    }
  };


  const runCleanupRule = async (preview = true) => {
    const payload = {
      profession: segmentFilter || undefined,
      remove_if_has: _parseRuleList(cleanupRule.remove_if_has),
      keep_if_has: _parseRuleList(cleanupRule.keep_if_has),
      require_any_of: _parseRuleList(cleanupRule.require_any_of),
      keep_if_tamil: !!cleanupRule.keep_if_tamil,
      preview,
    };
    // All 3 keyword fields are optional. If none are filled, the call is
    // effectively a no-op (nothing matches) and we just inform the admin.
    setCleanupRunning(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/marketing/contacts/cleanup-segment`,
        payload, { withCredentials: true },
      );
      setCleanupPreview(data);
      if (preview) {
        if (data.matches === 0) toast.info(`Scanned ${data.scanned} — no contacts match this rule.`);
        else toast.success(`${data.matches} of ${data.scanned} contact${data.matches === 1 ? '' : 's'} match — review below, then "Apply".`);
      } else {
        toast.success(`Removed ${data.deleted} contact${data.deleted === 1 ? '' : 's'} · book now has ${data.total_contacts}.`);
        setCleanupOpen(false);
        await loadContacts();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Cleanup rule failed');
    } finally {
      setCleanupRunning(false);
    }
  };

  const sendToSegment = async (segmentName, segmentCount) => {
    if (!segmentCount) { toast.error('No contacts in this segment'); return; }
    if (!window.confirm(
      `Send the WhatsApp invite ("pro_join_request") to ALL ${segmentCount} contact${segmentCount === 1 ? '' : 's'} tagged "${segmentName || 'all professions'}"?`
    )) return;
    setSendingSegment(true);
    try {
      const params = new URLSearchParams({ limit: '1000' });
      if (segmentName) params.set('profession', segmentName);
      const list = await axios.get(
        `${BACKEND_URL}/api/admin/marketing/contacts?${params.toString()}`,
        { withCredentials: true },
      );
      const phones = (list.data?.items || []).map((c) => c.phone).filter(Boolean);
      if (!phones.length) {
        toast.warning('No phones found in that segment.');
        return;
      }
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/marketing/invite-bulk`,
        { name: name.trim() || (segmentName ? segmentName.split(' ').map((w) => w[0]?.toUpperCase() + w.slice(1)).join(' ') : 'Partner'),
          phones: phones.join(' ') },
        { withCredentials: true },
      );
      if (data?.sent_ok === data?.valid_count) {
        toast.success(`Blasted ${data.sent_ok} WhatsApp invite${data.sent_ok === 1 ? '' : 's'} to "${segmentName || 'all'}" segment.`,
          { duration: 8000 });
      } else {
        toast.warning(`${data?.sent_ok}/${data?.valid_count} sent · ${data?.failed} failed. See History tab.`,
          { duration: 10000 });
      }
      await Promise.all([loadHistory(), loadContacts()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Segment send failed');
    } finally {
      setSendingSegment(false);
    }
  };

  const sendToCity = async (cityName, cityCount) => {
    if (!cityCount) { toast.error('No contacts in this city'); return; }
    if (!window.confirm(
      `Send WhatsApp invite ("pro_join_request") to ALL ${cityCount} contact${cityCount === 1 ? '' : 's'} in "${cityName || 'all cities'}"${segmentFilter ? ` filtered by "${segmentFilter}"` : ''}?`
    )) return;
    setSendingSegment(true);
    try {
      const params = new URLSearchParams({ limit: '5000' });
      if (cityName) params.set('city', cityName);
      if (segmentFilter) params.set('profession', segmentFilter);
      const list = await axios.get(
        `${BACKEND_URL}/api/admin/marketing/contacts?${params.toString()}`,
        { withCredentials: true },
      );
      const phones = (list.data?.items || []).map((c) => c.phone).filter(Boolean);
      if (!phones.length) {
        toast.warning('No phones found for that city.');
        return;
      }
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/marketing/invite-bulk`,
        { name: name.trim() || 'Partner', phones: phones.join(' ') },
        { withCredentials: true },
      );
      if (data?.sent_ok === data?.valid_count) {
        toast.success(`Blasted ${data.sent_ok} WhatsApp invite${data.sent_ok === 1 ? '' : 's'} to "${cityName}".`,
          { duration: 8000 });
      } else {
        toast.warning(`${data?.sent_ok}/${data?.valid_count} sent · ${data?.failed} failed. See History tab.`,
          { duration: 10000 });
      }
      await Promise.all([loadHistory(), loadContacts()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'City blast failed');
    } finally {
      setSendingSegment(false);
    }
  };

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-marketing-invite-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <WhatsappLogo size={20} weight="fill" className="text-green-600" /> Marketing — Vendor / Pro Invites
        </CardTitle>
        <CardDescription>
          Send the approved <code className="px-1 bg-stone-100">pro_join_request</code> WhatsApp template.
          Paste a single phone or many (separated by space / comma / newline) — each gets a personal
          send with the same recipient name. All sends are logged + saved to the contact book.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Tabs */}
        <div className="flex border-b border-[#E8E1D5] mb-3">
          {[
            { k: 'compose', label: 'Compose' },
            { k: 'history', label: `History (${history.total})` },
            { k: 'contacts', label: `Contacts (${contacts.total})` },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === t.k ? 'border-brand-primary text-brand-primary' : 'border-transparent text-text-secondary hover:text-text-primary'}`}
              data-testid={`marketing-tab-${t.k}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* COMPOSE */}
        {tab === 'compose' && (
          <div className="space-y-3">
            <Input value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Recipient name (used for every number — e.g. Vendor Partner)"
              className="rounded-none border-[#E8E1D5]"
              data-testid="invite-name-input" />
            <Textarea value={phonesRaw}
              onChange={(e) => setPhonesRaw(e.target.value)}
              rows={4}
              placeholder="Paste numbers — separated by space, comma, or new line.
e.g. 9876543210, +91 9876500001
      9876500002 9876500003"
              className="rounded-none border-[#E8E1D5] font-mono text-sm"
              data-testid="invite-phones-textarea" />

            {/* Live preview chips */}
            {(validList.length > 0 || invalid.length > 0) && (
              <div className="flex flex-wrap gap-1.5">
                {validList.map((p) => (
                  <span key={p}
                    className="inline-flex items-center gap-1 text-[11px] bg-green-50 text-green-800 border border-green-200 px-2 py-0.5"
                    data-testid={`preview-valid-${p}`}>
                    <CheckCircle size={11} weight="fill" /> {p}
                  </span>
                ))}
                {invalid.map((t, i) => (
                  <span key={`bad-${i}`}
                    className="inline-flex items-center gap-1 text-[11px] bg-red-50 text-red-800 border border-red-200 px-2 py-0.5 line-through"
                    data-testid={`preview-invalid-${i}`}>
                    <XCircle size={11} weight="fill" /> {t}
                  </span>
                ))}
              </div>
            )}

            <Button onClick={send} disabled={!canSend}
              className="rounded-none bg-green-600 hover:bg-green-700 text-white disabled:opacity-60 w-full sm:w-auto"
              data-testid="invite-send-btn">
              {sending ? <Spinner size={14} className="mr-1 animate-spin" /> : <PaperPlaneTilt size={14} className="mr-1" weight="fill" />}
              Send to {validList.length} number{validList.length === 1 ? '' : 's'}
            </Button>
          </div>
        )}

        {/* HISTORY */}
        {tab === 'history' && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-text-secondary">
                Recent sends
              </div>
              <div className="text-xs text-text-secondary">
                {history.success_count}/{history.total} successful
                <button onClick={loadHistory}
                  className="ml-2 text-brand-primary underline"
                  data-testid="invite-refresh-btn">
                  <ArrowsClockwise size={11} className="inline mr-0.5" />refresh
                </button>
              </div>
            </div>
            {loadingHistory ? (
              <p className="text-sm text-text-secondary text-center py-4">Loading…</p>
            ) : history.items.length === 0 ? (
              <p className="text-sm text-text-secondary text-center py-4 border border-dashed border-[#E8E1D5]">
                No invites sent yet.
              </p>
            ) : (
              <div className="border border-[#E8E1D5] divide-y divide-[#F0EBE0] max-h-[420px] overflow-y-auto">
                {history.items.map((it, i) => (
                  <div key={`${it.phone}-${it.sent_at}`}
                    className="flex items-center gap-3 px-3 py-2"
                    data-testid={`invite-row-${i}`}>
                    {it.ok
                      ? <CheckCircle size={18} weight="fill" className="text-green-600 shrink-0" />
                      : <XCircle size={18} weight="fill" className="text-red-600 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">
                        {it.name} <span className="text-text-secondary font-normal">· +91 {it.phone}</span>
                      </div>
                      <div className="text-[11px] text-text-secondary truncate">
                        {fmtDate(it.sent_at)}
                        {it.msg91_request_id ? ` · req: ${it.msg91_request_id.slice(0, 12)}…` : ''}
                        {it.error_snippet ? ` · ${it.error_snippet}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CONTACTS */}
        {tab === 'contacts' && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1">
                <MagnifyingGlass size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                <Input value={contactQuery}
                  onChange={(e) => setContactQuery(e.target.value)}
                  placeholder="Search by name or number"
                  className="rounded-none border-[#E8E1D5] pl-9"
                  data-testid="contact-search" />
              </div>
              <button onClick={loadContacts}
                className="text-xs text-brand-primary underline"
                data-testid="contact-refresh">
                <ArrowsClockwise size={11} className="inline mr-0.5" />refresh
              </button>
              <button onClick={purgeLandlines}
                disabled={!contacts.items.length}
                className="text-xs text-amber-700 hover:text-amber-800 underline disabled:opacity-40 disabled:cursor-not-allowed"
                title="Remove landlines and any non-mobile entries"
                data-testid="contact-purge-landlines">
                Clean landlines
              </button>
              <button onClick={purgeHospitality}
                disabled={!contacts.items.length}
                className="text-xs text-amber-700 hover:text-amber-800 underline disabled:opacity-40 disabled:cursor-not-allowed"
                title="Remove hotel / cafe / restaurant / bar / rooms entries that aren't tagged event / catering / venue / chef / wedding"
                data-testid="contact-purge-hospitality">
                Clean hospitality
              </button>
              <button onClick={purgeJoined}
                disabled={!contacts.items.length}
                className="text-xs text-blue-700 hover:text-blue-800 underline disabled:opacity-40 disabled:cursor-not-allowed"
                title="Remove contacts who have already created a vendor/pro account on BeeWeddy"
                data-testid="contact-purge-joined">
                Clean joined
              </button>
              <button onClick={backfillCities}
                disabled={!contacts.items.length}
                className="text-xs text-purple-700 hover:text-purple-800 underline disabled:opacity-40 disabled:cursor-not-allowed"
                title="Group all contacts under their city (Chennai, Bangalore, …) based on Google's address"
                data-testid="contact-backfill-cities">
                Group by city
              </button>
              <button onClick={deleteAllContacts}
                disabled={!contacts.items.length}
                className="text-xs text-red-600 hover:text-red-700 underline disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="contact-delete-all">
                <Trash size={11} className="inline mr-0.5" />Delete all
              </button>
            </div>

            {/* Segment chips — one per profession tag. Click to filter; the
                green WhatsApp icon on each chip blasts the entire segment. */}
            {(contacts.segments?.length || 0) > 0 && (
              <div className="mb-3 border border-[#E8E1D5] rounded p-2 bg-sand-50" data-testid="segment-chips">
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-xs font-bold text-text-secondary">
                    Segments ({contacts.segments.length}) — click a chip to filter
                  </Label>
                  <div className="flex items-center gap-2">
                    <button onClick={toggleMergeMode}
                      className={`text-[10px] underline ${mergeMode ? 'text-brand-primary font-bold' : 'text-blue-700 hover:text-blue-800'}`}
                      title="Combine similar segments (e.g. wedding venues + Hotel venues → venues)"
                      data-testid="segment-merge-toggle">
                      {mergeMode ? '× Cancel merge' : 'Merge segments'}
                    </button>
                    <button onClick={() => openCleanupRule(segmentFilter)}
                      className="text-[10px] underline text-amber-700 hover:text-amber-800"
                      title="Apply a per-segment cleanup rule"
                      data-testid="segment-cleanup-rule">
                      Cleanup rule
                    </button>
                    {segmentFilter && (
                      <button onClick={() => setSegmentFilter('')}
                        className="text-[10px] underline text-brand-primary"
                        data-testid="segment-clear">
                        Clear filter
                      </button>
                    )}
                  </div>
                </div>

                {/* Merge-mode toolbar */}
                {mergeMode && (
                  <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs space-y-1.5" data-testid="merge-toolbar">
                    <div className="text-blue-900 font-semibold">
                      Click chips below to select sources ({mergeSelected.size} picked)
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-text-secondary">Merge into:</span>
                      <Input value={mergeTarget}
                        onChange={(e) => setMergeTarget(e.target.value)}
                        placeholder="e.g. venues"
                        className="rounded-none h-7 text-xs border-blue-300 flex-1 min-w-[180px]"
                        data-testid="merge-target-input" />
                      <Button onClick={runSegmentMerge}
                        disabled={mergeRunning || mergeSelected.size < 2 || !mergeTarget.trim()}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-none h-7 text-xs disabled:opacity-50"
                        data-testid="merge-apply">
                        {mergeRunning ? 'Merging…' : `Merge ${mergeSelected.size} → "${mergeTarget.trim() || '...'}"`}
                      </Button>
                    </div>
                    {mergeSelected.size > 0 && (
                      <div className="text-[10px] text-blue-800">
                        Selected: {Array.from(mergeSelected).join(' · ')}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {/* "All" chip resets the filter */}
                  <button
                    type="button"
                    onClick={() => setSegmentFilter('')}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-all ${!segmentFilter ? 'bg-text-primary text-sand-50 border-text-primary' : 'bg-white text-text-secondary border-[#E8E1D5] hover:border-brand-primary/40'}`}
                    data-testid="segment-chip-all"
                  >
                    All · {contacts.total}
                  </button>
                  {contacts.segments.map((s) => {
                    const active = segmentFilter === s.profession;
                    const picked = mergeSelected.has(s.profession);
                    if (mergeMode) {
                      // In merge mode each chip is a multi-select checkbox.
                      // The WhatsApp blast button is hidden to avoid mis-clicks.
                      return (
                        <button key={s.profession}
                          type="button"
                          onClick={() => toggleMergePick(s.profession)}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-all ${picked ? 'bg-blue-600 text-white border-blue-600 font-bold' : 'bg-white text-text-secondary border-blue-200 hover:border-blue-400'}`}
                          data-testid={`merge-pick-${s.profession.replace(/\s+/g, '-')}`}
                        >
                          {picked ? '✓ ' : ''}{s.profession} · {s.count}
                        </button>
                      );
                    }
                    return (
                      <div key={s.profession} className={`flex items-stretch rounded-full overflow-hidden border ${active ? 'border-brand-primary' : 'border-[#E8E1D5]'}`}>
                        <button
                          type="button"
                          onClick={() => setSegmentFilter(s.profession)}
                          className={`text-xs px-2.5 py-1 transition-all ${active ? 'bg-brand-primary text-sand-50' : 'bg-white text-text-secondary hover:bg-sand-100'}`}
                          data-testid={`segment-chip-${s.profession.replace(/\s+/g, '-')}`}
                        >
                          {s.profession} · {s.count}
                        </button>
                        <button
                          type="button"
                          onClick={() => sendToSegment(s.profession, s.count)}
                          disabled={sendingSegment}
                          title={`Send WhatsApp invite to all ${s.count} contacts tagged "${s.profession}"`}
                          className="px-2 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 flex items-center"
                          data-testid={`segment-send-${s.profession.replace(/\s+/g, '-')}`}
                        >
                          <WhatsappLogo size={12} weight="fill" />
                        </button>
                      </div>
                    );
                  })}
                </div>
                {segmentFilter && (
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-text-secondary">
                      Showing <strong>{contacts.items.length}</strong> in <strong>{segmentFilter}</strong>
                    </span>
                    <Button
                      onClick={() => {
                        const seg = contacts.segments.find((s) => s.profession === segmentFilter);
                        sendToSegment(segmentFilter, seg?.count || contacts.items.length);
                      }}
                      disabled={sendingSegment || contacts.items.length === 0}
                      className="bg-green-600 hover:bg-green-700 text-white rounded-none h-7 px-3 text-xs"
                      data-testid="segment-send-filtered"
                    >
                      <WhatsappLogo size={12} weight="fill" className="mr-1" />
                      {sendingSegment ? 'Blasting…' : `Send to all ${contacts.items.length} in "${segmentFilter}"`}
                    </Button>
                  </div>
                )}

                {/* Cleanup rule inline form */}
                {cleanupOpen && (
                  <div className="mt-3 p-3 border border-amber-300 bg-amber-50 rounded space-y-2" data-testid="cleanup-rule-panel">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-bold text-amber-900">
                        Cleanup rule {segmentFilter ? `for "${segmentFilter}"` : '(whole book)'}
                      </div>
                      <button onClick={() => { setCleanupOpen(false); setCleanupPreview(null); }}
                        className="text-[10px] underline text-text-secondary"
                        data-testid="cleanup-close">close</button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                      <div>
                        <Label className="text-[10px] font-bold text-text-secondary uppercase tracking-wider">Remove if name has</Label>
                        <Input value={cleanupRule.remove_if_has}
                          onChange={(e) => setCleanupRule({ ...cleanupRule, remove_if_has: e.target.value })}
                          placeholder="interior"
                          className="rounded-none h-8 text-xs border-[#E8E1D5]"
                          data-testid="cleanup-remove-input" />
                        <p className="text-[9px] text-text-secondary mt-0.5">Highest priority — comma-separated.</p>
                      </div>
                      <div>
                        <Label className="text-[10px] font-bold text-text-secondary uppercase tracking-wider">Keep if name has</Label>
                        <Input value={cleanupRule.keep_if_has}
                          onChange={(e) => setCleanupRule({ ...cleanupRule, keep_if_has: e.target.value })}
                          placeholder="wedding, event, balloon"
                          className="rounded-none h-8 text-xs border-[#E8E1D5]"
                          data-testid="cleanup-keep-input" />
                        <p className="text-[9px] text-text-secondary mt-0.5">Remove if name has NONE of these.</p>
                      </div>
                      <div>
                        <Label className="text-[10px] font-bold text-text-secondary uppercase tracking-wider">Require any of</Label>
                        <Input value={cleanupRule.require_any_of}
                          onChange={(e) => setCleanupRule({ ...cleanupRule, require_any_of: e.target.value })}
                          placeholder="decorator, event"
                          className="rounded-none h-8 text-xs border-[#E8E1D5]"
                          data-testid="cleanup-require-input" />
                        <p className="text-[9px] text-text-secondary mt-0.5">Remove if name has none of these.</p>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-amber-900 cursor-pointer">
                      <input type="checkbox"
                        checked={cleanupRule.keep_if_tamil}
                        onChange={(e) => setCleanupRule({ ...cleanupRule, keep_if_tamil: e.target.checked })}
                        className="h-3.5 w-3.5 accent-amber-700"
                        data-testid="cleanup-tamil-checkbox" />
                      Keep names that contain any Tamil character (ஆ-ஃ etc.)
                    </label>
                    <div className="flex flex-wrap items-center gap-2 justify-end">
                      <Button onClick={() => runCleanupRule(true)} disabled={cleanupRunning}
                        variant="outline"
                        className="rounded-none border-amber-300 text-amber-900 h-8 text-xs"
                        data-testid="cleanup-preview-btn">
                        {cleanupRunning ? 'Scanning…' : 'Preview matches'}
                      </Button>
                      <Button onClick={() => runCleanupRule(false)} disabled={cleanupRunning || !(cleanupPreview?.matches > 0)}
                        className="bg-red-600 hover:bg-red-700 text-white rounded-none h-8 text-xs disabled:opacity-50"
                        data-testid="cleanup-apply-btn">
                        {cleanupRunning ? 'Deleting…' : (cleanupPreview ? `Delete ${cleanupPreview.matches}` : 'Apply')}
                      </Button>
                    </div>
                    {cleanupPreview && (
                      <div className="text-xs">
                        <div className="text-amber-900 mb-1">
                          <strong>{cleanupPreview.matches}</strong> of {cleanupPreview.scanned} contact{cleanupPreview.scanned === 1 ? '' : 's'} match this rule.
                          {cleanupPreview.preview === false && (
                            <span className="ml-1 text-green-700">→ Deleted. Book now has {cleanupPreview.total_contacts}.</span>
                          )}
                        </div>
                        {(cleanupPreview.samples?.length || 0) > 0 && (
                          <div className="border border-amber-200 bg-white rounded max-h-[180px] overflow-y-auto">
                            <table className="w-full text-[11px]">
                              <thead className="bg-amber-100 text-amber-900 sticky top-0">
                                <tr><th className="p-1 text-left">Sample name</th><th className="p-1 text-left">Phone</th><th className="p-1 text-left">Reason</th></tr>
                              </thead>
                              <tbody>
                                {cleanupPreview.samples.map((s, i) => (
                                  <tr key={i} className="border-t border-amber-100">
                                    <td className="p-1">{s.name}</td>
                                    <td className="p-1 font-mono">{s.phone}</td>
                                    <td className="p-1 text-text-secondary">{s.reason}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* City chips — one per `search_cities` tag.
                Click to filter; the green icon blasts the whole city. */}
            {(contacts.cities?.length || 0) > 0 && (
              <div className="mb-3 border border-purple-200 rounded p-2 bg-purple-50" data-testid="city-chips">
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-xs font-bold text-purple-900">
                    Cities ({contacts.cities.length}) — click to filter, green icon blasts the whole city
                  </Label>
                  {cityFilter && (
                    <button onClick={() => setCityFilter('')}
                      className="text-[10px] underline text-purple-700"
                      data-testid="city-clear">Clear city</button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <button type="button"
                    onClick={() => setCityFilter('')}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-all ${!cityFilter ? 'bg-text-primary text-sand-50 border-text-primary' : 'bg-white text-text-secondary border-purple-200 hover:border-purple-400'}`}
                    data-testid="city-chip-all">
                    All cities · {contacts.total}
                  </button>
                  {contacts.cities.map((c) => {
                    const active = cityFilter === c.city;
                    return (
                      <div key={c.city} className={`flex items-stretch rounded-full overflow-hidden border ${active ? 'border-purple-600' : 'border-purple-200'}`}>
                        <button type="button"
                          onClick={() => setCityFilter(c.city)}
                          className={`text-xs px-2.5 py-1 transition-all ${active ? 'bg-purple-600 text-white' : 'bg-white text-text-secondary hover:bg-purple-100'}`}
                          data-testid={`city-chip-${c.city.replace(/\s+/g, '-')}`}>
                          {c.city} · {c.count}
                        </button>
                        <button type="button"
                          onClick={() => sendToCity(c.city, c.count)}
                          disabled={sendingSegment}
                          title={`Send WhatsApp invite to all ${c.count} contacts in "${c.city}"${segmentFilter ? ` (filtered by "${segmentFilter}")` : ''}`}
                          className="px-2 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 flex items-center"
                          data-testid={`city-send-${c.city.replace(/\s+/g, '-')}`}>
                          <WhatsappLogo size={12} weight="fill" />
                        </button>
                      </div>
                    );
                  })}
                </div>
                {(segmentFilter || cityFilter) && (
                  <div className="mt-2 text-[11px] text-purple-900">
                    Showing <strong>{contacts.items.length}</strong> contact{contacts.items.length === 1 ? '' : 's'}
                    {segmentFilter && <> in <strong>{segmentFilter}</strong></>}
                    {cityFilter && <> · city <strong>{cityFilter}</strong></>}
                  </div>
                )}
              </div>
            )}
            {loadingContacts ? (
              <p className="text-sm text-text-secondary text-center py-4">Loading…</p>
            ) : contacts.items.length === 0 ? (
              <p className="text-sm text-text-secondary text-center py-4 border border-dashed border-[#E8E1D5]">
                <AddressBook size={28} className="mx-auto mb-2 text-stone-400" />
                Contact book is empty. Send your first invite from the Compose tab.
              </p>
            ) : (
              <div className="border border-[#E8E1D5] divide-y divide-[#F0EBE0] max-h-[420px] overflow-y-auto">
                {contacts.items.map((c, i) => (
                  <div key={c.phone}
                    className="flex items-center gap-3 px-3 py-2"
                    data-testid={`contact-row-${i}`}>
                    <span className={`shrink-0 w-2 h-2 rounded-full ${c.last_status_ok ? 'bg-green-500' : 'bg-red-500'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">
                        {c.name} <span className="text-text-secondary font-normal">· +91 {c.phone}</span>
                        {c.source === 'google_places' && (
                          <span className="ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded">
                            from Maps
                          </span>
                        )}
                      </div>
                      {(c.professions?.length || c.search_cities?.length || c.search_areas?.length) ? (
                        <div className="flex flex-wrap gap-1 mt-0.5" data-testid={`contact-tags-${i}`}>
                          {(c.professions || []).map((p) => (
                            <span key={`p-${p}`} className="text-[9px] px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded">
                              {p}
                            </span>
                          ))}
                          {(c.search_cities || []).map((cy) => (
                            <span key={`c-${cy}`} className="text-[9px] px-1.5 py-0.5 bg-stone-50 text-stone-700 border border-stone-200 rounded">
                              {cy}
                            </span>
                          ))}
                          {(c.search_areas || []).map((a) => (
                            <span key={`a-${a}`} className="text-[9px] px-1.5 py-0.5 bg-stone-100 text-stone-700 border border-stone-200 rounded">
                              {a}
                            </span>
                          ))}
                        </div>
                      ) : null}
                      <div className="text-[11px] text-text-secondary mt-0.5">
                        {c.send_count || 0} send{(c.send_count || 0) === 1 ? '' : 's'}
                        {c.last_sent_at ? ` · last ${fmtDate(c.last_sent_at)}` : ' · not sent yet'}
                      </div>
                    </div>
                    <Button variant="outline"
                      onClick={() => resendFromContact(c)}
                      className="rounded-none border-[#E8E1D5] h-8 px-3 text-xs"
                      data-testid={`contact-resend-${i}`}>
                      Add to send
                    </Button>
                    <Button variant="outline"
                      onClick={() => deleteContact(c)}
                      title="Delete from contact book"
                      className="rounded-none border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 h-8 w-8 p-0 shrink-0"
                      data-testid={`contact-delete-${i}`}>
                      <Trash size={13} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminMarketingInviteCard;
